<script lang="ts">
  import type { DisplayView, PublicPlayer } from '@banger/shared';
  import { clockOffset } from '../lib/view';
  import { secondsLeft } from '../lib/countdown';
  import * as sfx from '../lib/sfx';
  import Spin from './Spin.svelte';
  import Listening from './Listening.svelte';
  import Reveal from './Reveal.svelte';
  import Standings from './Standings.svelte';
  import Banger from './Banger.svelte';
  import Between from './Between.svelte';
  import Finale from './Finale.svelte';
  import HostControls from './HostControls.svelte';

  let { view, onnewroom }: { view: DisplayView; onnewroom: () => void } = $props();

  const byId = $derived(new Map<string, PublicPlayer>(view.players.map((p) => [p.id, p])));
  const steals = $derived(
    view.events.flatMap((e) => {
      if (e.type !== 'steal') return [];
      const by = byId.get(e.by);
      const target = byId.get(e.target);
      return by && target ? [`${by.emoji} ${by.name} stole a square from ${target.emoji} ${target.name}!`] : [];
    }),
  );

  // One shared clock. A paused room has no deadline, so the last value is held.
  let now = $state(Date.now());
  $effect(() => {
    const t = setInterval(() => (now = Date.now()), 250);
    return () => clearInterval(t);
  });
  let secs = $state(0);
  $effect(() => {
    if (!view.paused) secs = secondsLeft(view.deadline, now, $clockOffset);
  });

  // Views repeat every 5 s; sounds fire once per phase change.
  let prevKey = '';
  $effect(() => {
    const key = `${view.gameIndex}:${view.roundNumber}:${view.phase}`;
    if (key === prevKey) return;
    prevKey = key;
    if (view.phase === 'reveal') sfx.reveal(view.reveal?.results.some((r) => r.correct) ?? false);
    else if (view.phase === 'standings' && view.events.some((e) => e.type === 'steal')) sfx.steal();
    else if (view.phase === 'banger') sfx.airHorn();
    else if (view.phase === 'finale') sfx.champion();
  });
</script>

<!-- Any click on the TV (e.g. after a reload) re-enables sound effects. -->
<div class="game" onpointerdown={sfx.unlock} role="presentation">
  {#if view.phase === 'spin'}
    {#key view.roundNumber}<Spin {view} />{/key}
  {:else if view.phase === 'answer'}
    <Listening {view} {secs} />
  {:else if view.phase === 'reveal'}
    <Reveal {view} {secs} {byId} {steals} />
  {:else if view.phase === 'standings'}
    <Standings {view} {byId} {steals} />
  {:else if view.phase === 'banger'}
    <Banger {view} {byId} {steals} />
  {:else if view.phase === 'between'}
    <Between {view} {secs} {byId} />
  {:else if view.phase === 'finale'}
    <Finale {view} {byId} {onnewroom} />
  {/if}

  {#if view.paused}
    <div class="paused" role="status"><span class="neon">PAUSED</span></div>
  {/if}
  {#if view.phase !== 'finale'}<HostControls {view} />{/if}
</div>

<style>
  .game { min-height: 100%; height: 100%; }
  .paused {
    position: fixed; inset: 0; z-index: 55; display: flex; align-items: center; justify-content: center;
    background: rgba(10, 6, 24, 0.72); backdrop-filter: blur(4px);
    font-size: clamp(4rem, 12vw, 10rem); font-weight: 900; letter-spacing: 0.1em;
  }
</style>
